// ============================================
// DoorCommandsPanel - Audit lệnh mở cửa remote (toàn hệ thống)
// ============================================
//
// GET /atwork/door-commands/ — không lọc theo cửa. Cho phép lọc nhanh theo
// trạng thái ở client. Đây là view tổng, khác tab audit theo-cửa ở trang chi tiết.

import { useMemo, useState } from 'react';
import { ATWORK_COMMAND_STATUS_OPTIONS } from '@conai/shared-constants';
import { useI18n } from '../../../i18n';
import { useAtWorkDoorCommands } from '../../../hooks';

const STATUS_COLOR: Record<string, string> = {
  green: 'bg-green-100 text-green-700',
  red: 'bg-red-100 text-red-700',
  orange: 'bg-amber-100 text-amber-700',
};

// Map value lệnh mở cửa -> key i18n (nhãn trong ATWORK_COMMAND_STATUS_OPTIONS
// là tiếng Anh cứng, không đổi theo ngôn ngữ).
const COMMAND_STATUS_KEY: Record<string, string> = {
  PENDING: 'doorCmdProcessing',
  SUCCESS: 'doorCmdSuccess',
  FAILED: 'doorCmdFailed',
};

function commandStatusLabel(value: string, t: (k: string) => string, fallback?: string): string {
  const key = COMMAND_STATUS_KEY[value];
  return key ? t(key) : (fallback ?? value);
}

function StatusBadge({ status }: { status: string }) {
  const { t } = useI18n();
  const opt = ATWORK_COMMAND_STATUS_OPTIONS.find((o) => o.value === status);
  const cls = opt ? STATUS_COLOR[opt.color] : 'bg-slate-100 text-slate-700';
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${cls}`}>
      {commandStatusLabel(status, t, opt?.label)}
    </span>
  );
}

export function DoorCommandsPanel({ limit = 100 }: { limit?: number }) {
  const { t } = useI18n();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const { data, isLoading, error } = useAtWorkDoorCommands({ limit });

  const rows = useMemo(() => {
    const all = data ?? [];
    return statusFilter ? all.filter((c) => c.status === statusFilter) : all;
  }, [data, statusFilter]);

  return (
    <div className="overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-6 py-4">
        <h3 className="text-base font-bold text-slate-900">{t('remoteDoorCommands')}</h3>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm text-slate-700 outline-none focus:border-primary-500"
        >
          <option value="">{t('allStatuses')}</option>
          {ATWORK_COMMAND_STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {commandStatusLabel(o.value, t, o.label)}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center p-10">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" />
        </div>
      ) : error ? (
        <div className="p-10 text-center text-sm text-red-600">{t('failedToLoadCommands')}</div>
      ) : rows.length === 0 ? (
        <div className="p-10 text-center text-slate-500">{t('noRemoteCommandsFound')}</div>
      ) : (
        <div className="max-h-[420px] overflow-y-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="sticky top-0 bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">{t('eventTime')}</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">{t('doorName')}</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">{t('requester')}</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">{t('source')}</th>
                <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-600">{t('status')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {rows.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 text-sm text-slate-500">{new Date(c.created_at).toLocaleString()}</td>
                  <td className="px-6 py-4 text-slate-700">{c.door_name ?? c.door_id}</td>
                  <td className="px-6 py-4 text-slate-700">{c.requested_by_name ?? '-'}</td>
                  <td className="px-6 py-4 text-slate-600">{c.source}</td>
                  <td className="px-6 py-4 text-center">
                    <StatusBadge status={c.status} />
                    {c.error_message && <p className="mt-1 text-xs text-red-500">{c.error_message}</p>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
